import type {
  BillingAddress,
  OrderSimple,
  WovarCheckout,
  WovarCheckoutLineItem,
  WovarProduct,
} from "@commercekit/api";
import { useLocale } from "@commercekit/remix/lib/hooks/useLocale";
import type { PropsWithChildren } from "react";
import { createContext, useCallback, useContext, useMemo } from "react";

const MAX_GTM_CATEGORIES = 4;

/** Display price: discount/sale price when available, otherwise regular price (matches product page logic). */
function getProductDisplayPrice(
  product:
    | { salePriceInclTax?: { value?: number } | null; priceInclTax?: { value?: number } | null }
    | null
    | undefined
): number | undefined {
  if (!product) return undefined;
  return product.salePriceInclTax?.value ?? product.priceInclTax?.value;
}

// GA4 ecommerce value: sum of price × quantity, rounded to cents.
const sumItemsValue = (
  items: { price?: number | null; quantity?: number | null }[] = []
) =>
  Math.round(
    items.reduce((acc, i) => acc + (i.price ?? 0) * (i.quantity ?? 0), 0) * 100
  ) / 100;

declare global {
  interface Window {
    dataLayer: any[];
    google_tag_manager: any[];
  }
}

export const DataLayerCtx = createContext<{
  brand: string;
  currencyCode: string;
}>({
  currencyCode: "",
  brand: "",
});
export const DataLayerCtxProvider = ({
  currencyCode,
  brand,
  children,
}: PropsWithChildren<{
  currencyCode: string;
  brand: string;
}>) => {
  const providerVal = useMemo(
    () => ({ currencyCode, brand }),
    [currencyCode, brand]
  );
  return <DataLayerCtx.Provider value={providerVal} children={children} />;
};

export const useDataLayer = () => {
  const push = useCallback((v: { event: string; [key: string]: any }, options?: { skipReset?: boolean }) => {
    if (
      typeof window !== "undefined" &&
      typeof window.dataLayer !== "undefined"
    ) {
      if (!options?.skipReset) {
        resetDataLayer();
      }
      window.dataLayer = window.dataLayer || [];
      if (!options?.skipReset && "ecommerce" in v) {
        window.dataLayer.push({ ecommerce: null });
      }
      window.dataLayer.push(v);
    }
  }, []);
  const { currencyCode, brand } = useContext(DataLayerCtx);
  return { currencyCode, push, brand };
};

export interface WvPageLoadPurchaseParams {
  products: WovarProduct[];
  billingAddress: BillingAddress;
  order?: OrderSimple | null;
  isReturningCustomer: boolean;
}

export const useWvPageLoadPurchase = () => {
  const { push, currencyCode, brand } = useDataLayer();
  const { localeFullName } = useLocale();
  return useCallback(
    (params: WvPageLoadPurchaseParams) => {
      const { products, billingAddress, order, isReturningCustomer } = params;

      const preparedItems = order?.lineItems?.map((item) => {
        const product = products.find((prod) => prod.sku === item?.sku);
        const item_categories: Record<string, string> = {};
        for (let i = 0; i < MAX_GTM_CATEGORIES; i++) {
          const key = i > 0 ? `item_category${i + 1}` : "item_category";
          item_categories[key] = product?.categories[i]?.name ?? "";
        }
        const displayPrice =
          getProductDisplayPrice(product) ?? item?.priceInclTax?.value;
        const imageUrl =
          product?.defaultImage?.urlOriginal ||
          product?.images?.[0]?.urlOriginal ||
          "";
        return { item, product, item_categories, displayPrice, imageUrl };
      });

      const lineItems = preparedItems?.map(
        ({ item, product, item_categories, displayPrice, imageUrl }) => ({
          id: product?.sku,
          item_id: product?.sku,
          item_name: product?.name,
          image_url: imageUrl,
          affiliation: brand,
          coupon: "",
          discount: "",
          item_brand: brand,
          ...item_categories,
          item_list_name: "",
          price: displayPrice,
          item_list_id: item.sku,
          location_id: "",
          quantity: item.quantity,
          item_stock: product?.stockLevel,
        })
      );

      const ecommerce = {
        currency: currencyCode,
        transaction_id: order?.orderNumber,
        value: order?.totalInclTax?.value ?? 0,
        coupon: order?.coupon || "",
        shipping: order?.shippingCostInclTax?.value ?? 0,
        tax: order?.taxTotal?.value ?? 0,
        items: lineItems,
        email: billingAddress?.email ?? "",
        phone_number: billingAddress?.phone ?? "",
        first_name: billingAddress?.firstName ?? "",
        last_name: billingAddress?.lastName ?? "",
        postal_code: billingAddress?.postalCode ?? "",
        country: billingAddress?.countryCode ?? "",
        customer_type: isReturningCustomer ? "returning" : "new",
      };

      const itemsCount = order?.lineItems?.reduce(
        (acc, item) => acc + item.quantity,
        0
      );

      push({
        event: "purchase",
        ecommerce,
      });

      preparedItems?.forEach(
        ({ item, product, item_categories, imageUrl }, index) => {
          const lineItemPrice = item?.priceInclTax?.value ?? 0;
          push(
            {
              event: `purchase_product${index + 1}`,
              ecommerce: {
                transaction_id: order?.orderNumber,
                currency: currencyCode,
                value: lineItemPrice * (item?.quantity ?? 0),
                email: billingAddress?.email || "",
                items: [
                  {
                    id: product?.sku,
                    item_id: product?.sku,
                    item_name: product?.name,
                    item_brand: brand,
                    affiliation: brand,
                    ...item_categories,
                    item_list_name: "",
                    price: lineItemPrice,
                    quantity: item?.quantity ?? 0,
                    item_stock: product?.stockLevel,
                    coupon: "",
                    discount: "",
                    image_url: imageUrl,
                  },
                ],
              },
            },
            { skipReset: true }
          );
        }
      );

      if (localeFullName === "nl-nl") {
        const rev = ["pushRevenue", ecommerce.value, itemsCount, "100485608"];

        if (window._conv_q === undefined) window._conv_q = [];
        window._conv_q.push(rev);
      }
    },
    [brand, push, currencyCode]
  );
};

export const useWvBeginCheckout = () => {
  const { push, currencyCode, brand } = useDataLayer();
  return useCallback(
    (checkout: WovarCheckout) => {
      const lineItems = checkout?.lineItems?.map((item) => {
        const item_categories: Record<string, string> = {};
        for (let i = 0; i < MAX_GTM_CATEGORIES; i++) {
          const key = i > 0 ? `item_category${i + 1}` : "item_category";
          item_categories[key] = item?.product?.categories[i]?.name ?? "";
        }
        // Checkout line products carry no price fields; the line price is the source.
        const displayPrice = item?.price?.value;
        return {
          id: item?.sku,
          item_id: item?.sku,
          item_name: item?.name,
          affiliation: brand,
          coupon: "",
          discount: "",
          item_brand: brand,
          ...item_categories,
          item_list_name: "",
          price: displayPrice,
          quantity: item?.quantity,
          item_stock: item?.product?.stockLevel,
          image_url: item?.image?.url || "",
        };
      });
      push({
        event: "begin_checkout",
        ecommerce: {
          currency: currencyCode,
          value: sumItemsValue(lineItems),
          coupon: checkout?.coupon,
          items: lineItems,
        },
      });
    },
    [brand, push, currencyCode]
  );
};

export const useWvKlaviyoBeginCheckout = () => {
  const { push, currencyCode, brand } = useDataLayer();
  return useCallback(
    (checkout: WovarCheckout, email: string) => {
      if (!email) return;
      const lineItems = checkout?.lineItems?.map((item) => {
        const item_categories: Record<string, string> = {};
        for (let i = 0; i < MAX_GTM_CATEGORIES; i++) {
          const key = i > 0 ? `item_category${i + 1}` : "item_category";
          item_categories[key] = item?.product?.categories[i]?.name ?? "";
        }
        // Checkout line products carry no price fields; the line price is the source.
        const displayPrice = item?.price?.value;
        return {
          id: item?.sku,
          item_id: item?.sku,
          item_name: item?.name,
          affiliation: brand,
          coupon: "",
          discount: "",
          item_brand: brand,
          ...item_categories,
          item_list_name: "",
          price: displayPrice,
          quantity: item?.quantity,
          item_stock: item?.product?.stockLevel,
          image_url: item?.image?.url || "",
        };
      });
      push({
        event: "begin_checkout_klv",
        ecommerce: {
          currency: currencyCode,
          value: checkout?.totalInclTax?.value,
          coupon: checkout?.coupon,
          email,
          items: lineItems,
        },
      });
    },
    [brand, push, currencyCode]
  );
};

export const useWvAddToCart = () => {
  const { push, currencyCode, brand } = useDataLayer();
  return useCallback(
    (
      product: WovarProduct,
      quantity: number,
      // Selected variant/unit of measure, when it differs from the parent product.
      overrides?: { price?: number; stockLevel?: number }
    ) => {
      const item_categories: Record<string, string> = {};
      for (let i = 0; i < MAX_GTM_CATEGORIES; i++) {
        const key = i > 0 ? `item_category${i + 1}` : "item_category";
        item_categories[key] = product?.categories[i]?.name ?? "";
      }
      const displayPrice =
        overrides?.price ?? getProductDisplayPrice(product);
      push({
        event: "add_to_cart",
        ecommerce: {
          currency: currencyCode,
          value: (displayPrice ?? product?.priceInclTax?.value ?? 0) * quantity,
          coupon: "",
          items: [
            {
              id: product?.sku,
              item_id: product?.sku,
              item_name: product?.name,
              affiliation: brand,
              coupon: "",
              discount: "",
              item_brand: brand,
              ...item_categories,
              item_list_name: "",
              price: displayPrice ?? product?.priceInclTax?.value,
              quantity,
              item_stock: overrides?.stockLevel ?? product?.stockLevel,
              image_url: product?.defaultImage?.urlOriginal || "",
            },
          ],
        },
      });
    },
    [brand, push, currencyCode]
  );
};

export const useWvViewCart = () => {
  const { push, currencyCode, brand } = useDataLayer();

  return useCallback(
    (checkout: WovarCheckout) => {
      const lineItems = checkout?.lineItems?.map((item) => {
        const item_categories: Record<string, string> = {};
        for (let i = 0; i < MAX_GTM_CATEGORIES; i++) {
          const key = i > 0 ? `item_category${i + 1}` : "item_category";
          item_categories[key] = item?.product?.categories[i]?.name ?? "";
        }
        // Checkout line products carry no price fields; the line price is the source.
        const displayPrice = item?.price?.value;
        return {
          id: item?.sku,
          item_id: item?.sku,
          item_name: item?.name,
          affiliation: brand,
          coupon: checkout?.coupon,
          discount: item?.productDiscountTotal?.value,
          index: "",
          item_brand: brand,
          ...item_categories,
          item_list_id: "",
          item_list_name: "",
          item_variant: "",
          location_id: "",
          price: displayPrice,
          quantity: item?.quantity,
          item_stock: item?.product?.stockLevel,
          image_url: item?.image?.url || "",
        };
      });
      push({
        event: "view_cart",
        ecommerce: {
          currency: currencyCode,
          value: sumItemsValue(lineItems),
          items: lineItems,
        },
      });
    },
    [brand, push, currencyCode]
  );
};

export const useWvRemoveFromCart = () => {
  const { push, currencyCode, brand } = useDataLayer();
  return useCallback(
    (lineItem: WovarCheckoutLineItem) => {
      const item_categories: Record<string, string> = {};
      for (let i = 0; i < MAX_GTM_CATEGORIES; i++) {
        const key = i > 0 ? `item_category${i + 1}` : "item_category";
        item_categories[key] = lineItem?.product?.categories[i]?.name ?? "";
      }
      const displayPrice = lineItem?.price?.value;
      push({
        event: "remove_from_cart",
        ecommerce: {
          currency: currencyCode,
          value: sumItemsValue([
            { price: displayPrice, quantity: lineItem?.quantity },
          ]),
          items: [
            {
              id: lineItem?.sku,
              item_id: lineItem?.sku,
              item_name: lineItem?.name,
              affiliation: brand,
              coupon: "",
              discount: "",
              index: "",
              item_brand: brand,
              ...item_categories,
              item_list_id: "",
              item_list_name: "",
              item_variant: "",
              location_id: "",
              price: displayPrice,
              quantity: lineItem?.quantity,
              item_stock: lineItem?.product?.stockLevel,
              image_url: lineItem?.image?.url || "",
            },
          ],
        },
      });
    },
    [brand, push, currencyCode]
  );
};

export const useWvViewItem = () => {
  const { push, currencyCode, brand } = useDataLayer();
  return useCallback(
    (product: WovarProduct) => {
      const item_categories: Record<string, string> = {};
      for (let i = 0; i < MAX_GTM_CATEGORIES; i++) {
        const key = i > 0 ? `item_category${i + 1}` : "item_category";
        item_categories[key] = product?.categories[i]?.name ?? "";
      }
      const displayPrice = getProductDisplayPrice(product);
      push({
        event: "view_item",
        ecommerce: {
          currency: currencyCode,
          value: displayPrice ?? product?.priceInclTax?.value,
          items: [
            {
              id: product?.sku,
              item_id: product?.sku,
              item_name: product?.name,
              affiliation: brand,
              coupon: "",
              discount: "",
              index: "",
              item_brand: brand,
              ...item_categories,
              item_list_id: "",
              item_list_name: "",
              item_variant: "",
              location_id: "",
              price: displayPrice ?? product?.priceInclTax?.value,
              quantity: 1,
              item_stock: product?.stockLevel,
              image_url: product?.defaultImage?.urlOriginal || "",
            },
          ],
        },
      });
    },
    [brand, push, currencyCode]
  );
};

export const useWvViewItemList = () => {
  const { push, currencyCode, brand } = useDataLayer();
  return useCallback(
    (products: WovarProduct[]) => {
      const itemList = products.map((item) => {
        const item_categories: Record<string, string> = {};
        for (let i = 0; i < MAX_GTM_CATEGORIES; i++) {
          const key = i > 0 ? `item_category${i + 1}` : "item_category";
          item_categories[key] = item?.categories[i]?.name ?? "";
        }
        const displayPrice = getProductDisplayPrice(item);
        return {
          id: item?.sku,
          item_id: item?.sku,
          item_name: item?.name,
          affiliation: brand,
          coupon: "",
          discount: item?.discountedPriceInclTax?.value,
          index: "",
          item_brand: brand,
          ...item_categories,
          item_list_id: "",
          item_list_name: "",
          item_variant: "",
          location_id: "",
          price: displayPrice ?? item?.priceInclTax?.value,
          quantity: 1,
          item_stock: item?.stockLevel,
          image_url: item?.defaultImage?.urlOriginal || "",
        };
      });
      push({
        event: "view_item_list",
        ecommerce: {
          currency: currencyCode,
          value: "",
          items: itemList,
        },
      });
    },
    [brand, push, currencyCode]
  );
};

export const useWvLogin = () => {
  const { push } = useDataLayer();
  return useCallback(
    (email?: string) => {
      push({
        event: "login",
        ecommerce: {
          ...(email ? { email } : {}),
        },
      });
    },
    [push]
  );
};

export const useWvSelectItem = () => {
  const { push, brand } = useDataLayer();
  return useCallback(
    (product: WovarProduct, itemListId: string, itemListName: string) => {
      const item_categories: Record<string, string> = {};
      for (let i = 0; i < MAX_GTM_CATEGORIES; i++) {
        const key = i > 0 ? `item_category${i + 1}` : "item_category";
        item_categories[key] = product?.categories[i]?.name ?? "";
      }
      const displayPrice = getProductDisplayPrice(product);
      push({
        event: "select_item",
        ecommerce: {
          item_list_id: itemListId,
          item_list_name: itemListName,
          items: [
            {
              id: product?.sku,
              item_id: product?.sku,
              item_name: product?.name,
              affiliation: brand,
              coupon: "",
              discount: "",
              index: 0,
              item_brand: brand,
              ...item_categories,
              item_list_id: itemListId,
              item_list_name: itemListName,
              item_variant: "",
              location_id: "",
              price: displayPrice ?? product?.priceInclTax?.value,
              quantity: 1,
              item_stock: product?.stockLevel,
              image_url: product?.defaultImage?.urlOriginal || "",
            },
          ],
        },
      });
    },
    [brand, push]
  );
};

export interface WvSignUpParams {
  email?: string;
  phone_number?: string;
  first_name?: string;
  last_name?: string;
}

export const useWvSignUp = () => {
  const { push } = useDataLayer();
  return useCallback(
    (params?: WvSignUpParams) => {
      push({
        event: "sign_up",
        ecommerce: {
          email: params?.email ?? "",
          phone_number: params?.phone_number ?? "",
          first_name: params?.first_name ?? "",
          last_name: params?.last_name ?? "",
        },
      });
    },
    [push]
  );
};


export interface WvContactFormParams {
  email: string;
  phone_number?: string;
  first_name?: string;
}

export const useWvContactForm = () => {
  const { push } = useDataLayer();
  return useCallback(
    (params: WvContactFormParams) => {
      push({
        event: "contact_form",
        ecommerce: {
          email: params.email,
          phone_number: params.phone_number ?? "",
          first_name: params.first_name ?? "",
        },
      });
    },
    [push]
  );
};

export const useWvNotifyStock = () => {
  const { push, currencyCode, brand } = useDataLayer();
  return useCallback(
    (product: WovarProduct, email: string) => {
      const item_categories: Record<string, string> = {};
      for (let i = 0; i < MAX_GTM_CATEGORIES; i++) {
        const key = i > 0 ? `item_category${i + 1}` : "item_category";
        item_categories[key] = product?.categories[i]?.name ?? "";
      }
      const displayPrice = getProductDisplayPrice(product);
      push({
        event: "notify_stock",
        ecommerce: {
          currency: currencyCode,
          value: displayPrice ?? product?.priceInclTax?.value ?? 0,
          items: [
            {
              id: product?.sku,
              item_id: product?.sku,
              item_name: product?.name,
              image_url: product?.defaultImage?.urlOriginal ?? "",
              affiliation: brand,
              coupon: "",
              discount: "",
              item_brand: brand,
              ...item_categories,
              item_list_name: "",
              price: displayPrice ?? product?.priceInclTax?.value,
              quantity: 1,
              item_stock: product?.stockLevel,
            },
          ],
          email,
        },
      });
    },
    [brand, push, currencyCode]
  );
};

export const useWvResetPassword = () => {
  const { push } = useDataLayer();
  return useCallback(
    (email: string) => {
      push({
        event: "reset_password",
        ecommerce: {
          email,
        },
      });
    },
    [push]
  );
};

export const useWvSearch = () => {
  const { push } = useDataLayer();
  return useCallback((searchTerm: string, resultsCount: string) => {
    push({
      event: "view_search_results",
      search_term: searchTerm,
      results_count: resultsCount,
    });
  }, [push]);
};

interface WvSearchFiltersParams {
  currentSearch: string;
  previousSearch: string | undefined;
  resultsCount: number;
  categoryName: string;
}

const getFilters = (search: string) => {
  const searchParams = new URLSearchParams(search);
  const filter = searchParams.get("filter");
  const decodedFilter = decodeURIComponent(filter || "");
  return decodedFilter
    .split(";")
    .filter((pair) => pair.trim().length > 0)
    .map((pair) => {
      const [rawKey, ...rawValueParts] = pair.split(":");
      const key = (rawKey ?? "").trim();
      const valueRaw = rawValueParts.join(":").trim();
      const value: string | string[] = valueRaw.includes(",")
        ? valueRaw
            .split(",")
            .map((v) => v.trim())
            .filter((v) => v.length > 0)
        : valueRaw;
      return key ? { key, value } : null;
    })
    .filter((entry): entry is { key: string; value: string | string[] } => !!entry);
};

/** Collect per-key value arrays from filter list (value may be string or string[]) */
const getValuesByKey = (
  filters: { key: string; value: string | string[] }[]
): Record<string, string[]> => {
  const map: Record<string, string[]> = {};
  for (const f of filters) {
    const arr = Array.isArray(f.value) ? [...f.value] : [f.value];
    if (!map[f.key]) map[f.key] = [];
    map[f.key].push(...arr);
  }
  return map;
};

/** One filter change: a single value added or removed for a key */
const getModifiedFilter = (
  currentFilters: { key: string; value: string | string[] }[],
  prevFilters: { key: string; value: string | string[] }[]
): { key: string; value: string; isAdded: boolean } | null => {
  const currentByKey = getValuesByKey(currentFilters);
  const prevByKey = getValuesByKey(prevFilters);
  const allKeys = new Set([
    ...Object.keys(currentByKey),
    ...Object.keys(prevByKey),
  ]);

  for (const key of allKeys) {
    const currentSet = new Set(currentByKey[key] ?? []);
    const prevSet = new Set(prevByKey[key] ?? []);
    const added = [...currentSet].filter((v) => !prevSet.has(v));
    const removed = [...prevSet].filter((v) => !currentSet.has(v));
    if (added.length > 0) {
      return { key, value: added[0], isAdded: true };
    }
    if (removed.length > 0) {
      return { key, value: removed[0], isAdded: false };
    }
  }
  return null;
};

export const useWvSearchFilters = () => {
  const { push } = useDataLayer();
  return useCallback((params: WvSearchFiltersParams) => {
    const { currentSearch, previousSearch, resultsCount, categoryName } = params;

    const currentFilters = getFilters(currentSearch);
    const prevFilters = previousSearch ? getFilters(previousSearch) : [];

    const modifiedFilter = getModifiedFilter(currentFilters, prevFilters);
    if (!modifiedFilter) {
      return;
    }

    const valueCount = (f: { key: string; value: string | string[] }) =>
      Array.isArray(f.value) ? f.value.length : 1;

    const event = modifiedFilter.isAdded ? "filter_applied" : "filter_removed";
    const filtersActive = currentFilters.reduce((acc, filter) => {
      return { ...acc, [filter.key]: filter.value };
    }, {} as Record<string, string | string[]>);

    push({
      event,
      filter_group: modifiedFilter.key,
      filter_name: modifiedFilter.key,
      filter_value: modifiedFilter.value,
      filters_active: filtersActive,
      filters_count: currentFilters.reduce(
        (acc, filter) => acc + valueCount(filter),
        0
      ),
      listing_context: categoryName,
      results_count: resultsCount,
    });
  }, [push]);
};

export const useWv404Page = () => {
  const { push } = useDataLayer();
  return useCallback(() => {
    push({
      event: "error_page_view",
      error_type: "404",
      http_status: "404",
      page_location: window.location.href,
      page_referrer: document.referrer || "(direct)",
    });
  }, [push]);
};

export const useWvPromoBarClick = () => {
  const { push } = useDataLayer();
  return useCallback((promoName: string, promoLink: string) => {
    push({
      event: "promo_bar_click",
      promo_name: promoName,
      destination_url: promoLink,
    });
  }, [push]);
};

const resetDataLayer = () => {
  if (
    window.dataLayer !== undefined &&
    window.google_tag_manager !== undefined
  ) {
    window.dataLayer.length = 0;
    const gtmContainerReg = /GTM-/i;

    for (const gtmKey of Object.keys(window.google_tag_manager)) {
      if (gtmContainerReg.test(gtmKey)) {
        window.google_tag_manager[gtmKey]?.dataLayer?.reset();
      }
    }
  }
};
